import { toast } from "./sonner.tsx";

export type ShareOutcome = {
    method: "native" | "clipboard";
    status: "completed" | "cancelled" | "failed";
    error?: string;
};

function cleanShareUrl(raw: string): string {
    const url = new URL(raw);
    url.searchParams.delete("conversationId");
    return url.toString();
}

function errorName(error: unknown): string {
    return typeof error === "object" &&
        error !== null &&
        "name" in error &&
        typeof error.name === "string"
        ? error.name
        : "unknown";
}

export function canNativeShare(): boolean {
    return typeof navigator !== "undefined" && Boolean(navigator.share);
}

export async function shareOrCopy(
    title: string,
    url = window.location.href,
): Promise<ShareOutcome> {
    const clean = cleanShareUrl(url);
    if (!canNativeShare()) {
        try {
            await navigator.clipboard.writeText(clean);
        } catch (error) {
            toast.error("Couldn't copy the link");
            return {
                method: "clipboard",
                status: "failed",
                error: errorName(error),
            };
        }
        toast.success("Link copied");
        return { method: "clipboard", status: "completed" };
    }
    try {
        await navigator.share({ title, url: clean });
    } catch (error) {
        const name = errorName(error);
        if (name === "AbortError" || name === "InvalidStateError") {
            return { method: "native", status: "cancelled" };
        }
        return { method: "native", status: "failed", error: name };
    }
    return { method: "native", status: "completed" };
}
